import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lightbulb,
  Sparkles,
  Clock,
  Target,
  Zap,
  ChevronRight,
  X,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Loader2,
} from 'lucide-react';
import { api, handleApiError } from '@/services/client';
import { skillService } from '@/services/skills';
import { EmbeddedSkillRail } from '@/components/skill';

interface SmartSuggestion {
  id: string;
  type: 'tab_close' | 'tab_group' | 'focus_reminder' | 'workflow' | 'break';
  title: string;
  description: string;
  reason: string;
  confidence: number;
  actionLabel: string;
  icon: string;
  createdAt: string;
}

interface SuggestionRule {
  id: string;
  name: string;
  enabled: boolean;
  condition: string;
}

function mapSuggestion(raw: Record<string, unknown>): SmartSuggestion {
  return {
    id: String(raw.id),
    type: (raw.type as SmartSuggestion['type']) ?? 'workflow',
    title: String(raw.title ?? raw.name ?? 'Suggestion'),
    description: String(raw.description ?? ''),
    reason: String(raw.reason ?? ''),
    confidence: Number(raw.confidence ?? 0),
    actionLabel: String(raw.actionLabel ?? raw.action_label ?? 'Apply'),
    icon: String(raw.icon ?? ''),
    createdAt: String(raw.created_at ?? raw.createdAt ?? new Date().toISOString()),
  };
}

function openCommandPalette() {
  window.dispatchEvent(
    new KeyboardEvent('keydown', {
      key: 'k',
      code: 'KeyK',
      metaKey: true,
      ctrlKey: true,
      bubbles: true,
    }),
  );
}

export const SmartSuggestionsPage = () => {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<SmartSuggestion[]>([]);
  const [rules, setRules] = useState<SuggestionRule[]>([]);
  const [filter, setFilter] = useState<'all' | 'tabs' | 'focus' | 'workflow'>('all');
  const [loading, setLoading] = useState(true);
  const [executingId, setExecutingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchSuggestions = useCallback(async () => {
    setError(null);
    try {
      const response = await api.get<{ suggestions: Record<string, unknown>[] }>('/smart-actions/suggestions');
      const rows = response.data?.suggestions ?? [];
      setSuggestions(rows.map((r) => mapSuggestion(r)));
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRules = useCallback(async () => {
    try {
      const response = await api.get<{ rules: SuggestionRule[] }>('/smart-actions/rules');
      setRules(response.data?.rules ?? []);
    } catch (err) {
      console.error('Failed to fetch rules:', err);
    }
  }, []);

  useEffect(() => {
    fetchSuggestions();
    fetchRules();
  }, [fetchSuggestions, fetchRules]);

  const executeSuggestion = async (suggestion: SmartSuggestion) => {
    setError(null);
    setSuccess(null);
    setExecutingId(suggestion.id);

    try {
      if (suggestion.type === 'focus_reminder') {
        navigate('/productivity?section=pomodoro');
        setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
        return;
      }
      if (suggestion.type === 'break') {
        navigate('/productivity?section=pomodoro');
        setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
        return;
      }
      if (suggestion.type === 'workflow' && suggestion.title.toLowerCase().includes('gentab')) {
        await skillService.executeSkill('generate_app', {});
        navigate('/apps/generate');
        setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
        setSuccess('GenTab workflow started.');
        return;
      }
      if (suggestion.type === 'tab_close' && suggestion.title.toLowerCase().includes('duplicate')) {
        await skillService.executeSkill('close_duplicates', {});
      } else if (suggestion.type === 'tab_group') {
        await skillService.executeSkill('group_by_domain', {});
      } else {
        await api.post('/smart-actions/execute', { suggestion_id: suggestion.id });
      }
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
      setSuccess(`"${suggestion.title}" applied.`);
      await fetchSuggestions();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setExecutingId(null);
    }
  };

  const dismissSuggestion = async (id: string) => {
    setSuggestions((prev) => prev.filter((s) => s.id !== id));
  };

  const toggleRule = async (rule: SuggestionRule) => {
    const next = !rule.enabled;
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, enabled: next } : r)));
    try {
      await api.put(`/smart-actions/rules/${rule.id}`, { enabled: next });
      await fetchSuggestions();
    } catch (err) {
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, enabled: rule.enabled } : r)));
      setError(handleApiError(err));
    }
  };

  const runQuickAction = async (action: 'organize' | 'clutter' | 'focus') => {
    setError(null);
    setSuccess(null);
    try {
      if (action === 'organize') {
        await skillService.executeSkill('auto_organize', {});
        setSuccess('Tab organization started.');
        navigate('/tabs');
      } else if (action === 'clutter') {
        await skillService.executeSkill('close_duplicates', {});
        setSuccess('Duplicate tabs cleared.');
        await fetchSuggestions();
      } else {
        navigate('/productivity?section=pomodoro');
      }
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'tab_close': return <X className="w-5 h-5" />;
      case 'focus_reminder': return <Clock className="w-5 h-5" />;
      case 'tab_group': return <Target className="w-5 h-5" />;
      case 'workflow': return <TrendingUp className="w-5 h-5" />;
      case 'break': return <Sparkles className="w-5 h-5" />;
      default: return <Lightbulb className="w-5 h-5" />;
    }
  };

  const getIconBg = (type: string) => {
    switch (type) {
      case 'tab_close': return 'bg-red-500/20 text-red-400';
      case 'focus_reminder': return 'bg-blue-500/20 text-blue-400';
      case 'tab_group': return 'bg-purple-500/20 text-purple-400';
      case 'workflow': return 'bg-green-500/20 text-green-400';
      case 'break': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-khiimori-bg-tertiary/20 text-khiimori-text-muted';
    }
  };

  const filteredSuggestions = suggestions.filter((s) => {
    if (filter === 'all') return true;
    if (filter === 'tabs') return s.type === 'tab_close' || s.type === 'tab_group';
    if (filter === 'focus') return s.type === 'focus_reminder' || s.type === 'break';
    if (filter === 'workflow') return s.type === 'workflow';
    return true;
  });

  return (
    <div className="space-y-6">
        {(error || success) && (
          <div className={`rounded-lg border px-4 py-2 text-sm ${
            error ? 'border-red-500/40 bg-red-500/10 text-red-300' : 'border-success-500/40 bg-success-500/10 text-success-300'
          }`}>
            {error || success}
          </div>
        )}

        <div className="bg-khiimori-bg-secondary rounded-2xl shadow-lg p-8 border border-khiimori-border">
          <div className="flex items-center justify-between mb-6">
            <button
              type="button"
              onClick={openCommandPalette}
              className="px-6 py-3 bg-gradient-to-r from-yellow-600 to-amber-600 text-white rounded-xl font-semibold hover:from-yellow-700 hover:to-amber-700 transition-colors shadow-lg flex items-center gap-2"
            >
              <Zap className="w-5 h-5" />
              Command Palette
            </button>
            <button
              type="button"
              onClick={() => { setLoading(true); void fetchSuggestions(); }}
              className="text-sm text-khiimori-text-muted hover:text-khiimori-text-secondary"
            >
              Refresh
            </button>
          </div>

          <div className="sticky top-11 z-20 -mx-2 flex gap-2 overflow-x-auto bg-khiimori-bg-secondary px-2 py-2">
            {([
              { key: 'all', label: 'All Suggestions' },
              { key: 'tabs', label: 'Tab Management' },
              { key: 'focus', label: 'Focus & Breaks' },
              { key: 'workflow', label: 'Workflow' },
            ] as const).map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setFilter(tab.key)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filter === tab.key
                    ? 'bg-gradient-to-r from-yellow-600 to-amber-600 text-white shadow-lg'
                    : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary hover:bg-khiimori-bg-elevated'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <EmbeddedSkillRail
          title="Related Skills"
          description="Skills you can run to organise, optimise, and predict."
          skillIds={['smart_suggestions', 'auto_organize', 'close_duplicates', 'analyze_workspace', 'predict_tabs', 'smart_preload']}
        />

        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-khiimori-text-muted gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Analyzing your workspace…
            </div>
          ) : (
            <>
              {filteredSuggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  className="bg-khiimori-bg-secondary rounded-2xl shadow-md p-6 border border-khiimori-border hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${getIconBg(suggestion.type)}`}>
                      {getIcon(suggestion.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-bold text-khiimori-text-primary">{suggestion.title}</h3>
                        <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded-full">
                          {suggestion.confidence}% confidence
                        </span>
                      </div>
                      <p className="text-khiimori-text-muted mb-3">{suggestion.description}</p>
                      <div className="flex items-center gap-2 text-sm text-khiimori-text-muted">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span>{suggestion.reason}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        type="button"
                        disabled={executingId === suggestion.id}
                        onClick={() => void executeSuggestion(suggestion)}
                        className="px-4 py-2 bg-gradient-to-r from-yellow-600 to-amber-600 text-white rounded-lg font-medium hover:from-yellow-700 hover:to-amber-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        {executingId === suggestion.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            {suggestion.actionLabel}
                            <ChevronRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => dismissSuggestion(suggestion.id)}
                        className="px-4 py-2 bg-khiimori-bg-tertiary text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-elevated transition-colors text-sm"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {filteredSuggestions.length === 0 && (
                <div className="bg-khiimori-bg-secondary rounded-2xl shadow-md p-12 text-center border border-khiimori-border">
                  <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-khiimori-text-primary mb-2">No suggestions right now</h3>
                  <p className="text-khiimori-text-muted">
                    You're doing great! Suggestions appear when your open tabs match enabled rules below.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="bg-khiimori-bg-secondary rounded-xl shadow-md p-6 border border-khiimori-border">
          <h3 className="text-lg font-bold text-khiimori-text-primary mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500" />
            Suggestion Rules
          </h3>
          <div className="space-y-3">
            {rules.map((rule) => (
              <div
                key={rule.id}
                className="flex items-center justify-between p-4 bg-khiimori-bg-tertiary rounded-xl"
              >
                <div className="flex-1">
                  <div className="font-medium text-khiimori-text-primary">{rule.name}</div>
                  <div className="text-sm text-khiimori-text-muted">{rule.condition}</div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={rule.enabled}
                  onClick={() => void toggleRule(rule)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    rule.enabled ? 'bg-yellow-500' : 'bg-khiimori-bg-elevated'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      rule.enabled ? 'left-7' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl shadow-lg p-6 text-white">
          <h3 className="text-lg font-bold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <button
              type="button"
              onClick={() => void runQuickAction('organize')}
              className="p-4 bg-khiimori-bg-elevated/30 hover:bg-khiimori-bg-elevated/50 rounded-xl transition-colors text-left"
            >
              <div className="font-medium">Organize Tabs</div>
              <div className="text-sm text-khiimori-text-primary/80">Group similar tabs</div>
            </button>
            <button
              type="button"
              onClick={() => void runQuickAction('clutter')}
              className="p-4 bg-khiimori-bg-elevated/30 hover:bg-khiimori-bg-elevated/50 rounded-xl transition-colors text-left"
            >
              <div className="font-medium">Clear Clutter</div>
              <div className="text-sm text-khiimori-text-primary/80">Close duplicates</div>
            </button>
            <button
              type="button"
              onClick={() => void runQuickAction('focus')}
              className="p-4 bg-khiimori-bg-elevated/30 hover:bg-khiimori-bg-elevated/50 rounded-xl transition-colors text-left"
            >
              <div className="font-medium">Start Focus</div>
              <div className="text-sm text-khiimori-text-primary/80">Begin Pomodoro</div>
            </button>
          </div>
        </div>
    </div>
  );
};
